import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Grid,
  Icon,
  Label,
  Loader,
  Statistic,
  Table,
  Popup,
  Dimmer,
} from 'semantic-ui-react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { iRootState } from '../../../store';
import {
  AvailableBrokersAndEstatesState,
} from '../../../store/types/temp-types';
import { CustoDropDownItemProps } from '../../../components/types';
import AuthorizationContainer from '../../../components/AuthorizationContainer';
import { ActionEnum } from '../../../enums/authz-action.enum';
import { FeatureEnum } from '../../../enums/authz-feature.enum';
import { get } from '../../../services/request';
import {
  downloadYelumSpreadsheet,
  downloadYelumConsolidatedSpreadsheet,
  setBatchManualProcessing,
  UpdatedBatchCodesResponse,
} from '../../../services/fire-insurance-batches';
import EditBatchCodesModal from './components/EditBatchCodesModal';
import TransmitBatchConfirmModal from './components/TransmitBatchConfirmModal';
import SyncBatchNumberingModal from './components/SyncBatchNumberingModal';
import { canTransmitBatch } from './utils/batch-transmit.util';
import { canSyncBatchNumbering } from './utils/batch-sync-numbering.util';
import moment from 'moment-timezone';
import {
  BatchDashboardItem,
  BatchDashboardStats,
  BatchStatusOptions,
  getBatchStatusColor,
  translateBatchStatus,
} from './types';
import Button from '../../../components/Button';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { dangerNotification, successNotification } from '../../../services/notification';
import FilterBar, { FilterField, FilterValues } from '../../../components/Filter';

const PageContainer = styled.div`
  padding: 20px;
`;

const StatsCardContainer = styled.div`
  margin-bottom: 30px;
`;

const StatsCard = styled(Card)`
  &.ui.card {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
  }
`;

const TableContainer = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ClickableTableRow = styled(Table.Row)<{ $manualOpen?: boolean }>`
  cursor: pointer;
  background-color: ${(props) => (props.$manualOpen ? '#f8f4ff' : 'inherit')};
  :hover {
    background-color: ${(props) => (props.$manualOpen ? '#f0e8ff' : '#f0f0f0')} !important;
  }
`;

const AlertBadge = styled(Label)`
  margin: 0 4px 4px 0 !important;
`;

const ActionsCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

type Props = {
  availableBrokersAndEstates: AvailableBrokersAndEstatesState;
};

const initialFilterValues: FilterValues = {
  search: '',
  estateId: '',
  status: '',
  showSingleItemOnly: false,
  showNearDeadlineOnly: false,
  showManualOnly: false,
};

const FireInsuranceBatchesDashboard = (props: Props) => {
  const { availableBrokersAndEstates } = props;
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [downloadingBatchId, setDownloadingBatchId] = useState<string | null>(null);
  const [exportingConsolidated, setExportingConsolidated] = useState<boolean>(false);
  const [togglingBatchId, setTogglingBatchId] = useState<string | null>(null);
  const [editingBatch, setEditingBatch] = useState<BatchDashboardItem | null>(null);
  const [transmittingBatch, setTransmittingBatch] = useState<BatchDashboardItem | null>(null);
  const [syncingBatch, setSyncingBatch] = useState<BatchDashboardItem | null>(null);
  const [stats, setStats] = useState<BatchDashboardStats | null>(null);
  const [batches, setBatches] = useState<BatchDashboardItem[]>([]);
  const [filteredBatches, setFilteredBatches] = useState<BatchDashboardItem[]>([]);

  const [appliedFilters, setAppliedFilters] = useState<FilterValues>(initialFilterValues);

  const history = useHistory();
  const match = useRouteMatch();

  const estateOptions: CustoDropDownItemProps[] = useMemo(
    () =>
      (availableBrokersAndEstates?.estates || []).map((item) => {
        const valueId =
          typeof item.value === 'string' ? item.value : item.value?.id;

        return {
          key: item.key,
          text: item.text,
          value: valueId || '',
        };
      }),
    [availableBrokersAndEstates?.estates],
  );

  const filterFields: FilterField[] = useMemo(
    () => [
      {
        kind: 'text',
        name: 'search',
        label: 'Buscar',
        placeholder: 'Número do lote ou nome da imobiliária...',
        width: 1.4,
      },
      {
        kind: 'select',
        name: 'estateId',
        label: 'Imobiliária',
        placeholder: 'Todas',
        options: estateOptions.map((option) => ({
          value: option.value,
          label: option.text,
        })),
        getDisplayValue: (values) => {
          const option = estateOptions.find(
            (item) => item.value === values.estateId,
          );
          return option ? option.text : undefined;
        },
      },
      {
        kind: 'select',
        name: 'status',
        label: 'Status',
        placeholder: 'Todos',
        options: BatchStatusOptions.map((option) => ({
          value: option.value,
          label: option.text,
        })),
        getDisplayValue: (values) => {
          const option = BatchStatusOptions.find(
            (item) => item.value === values.status,
          );
          return option ? option.text : undefined;
        },
      },
      {
        kind: 'checkbox',
        name: 'showSingleItemOnly',
        label: 'Apenas com 1 item',
        advanced: true,
        getDisplayValue: (values) => (values.showSingleItemOnly ? 'Sim' : undefined),
      },
      {
        kind: 'checkbox',
        name: 'showNearDeadlineOnly',
        label: 'Próximos do prazo',
        advanced: true,
        getDisplayValue: (values) => (values.showNearDeadlineOnly ? 'Sim' : undefined),
      },
      {
        kind: 'checkbox',
        name: 'showManualOnly',
        label: 'Apenas processamento manual',
        advanced: true,
        getDisplayValue: (values) => (values.showManualOnly ? 'Sim' : undefined),
      },
    ],
    [estateOptions],
  );

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    let filtered = [...batches];

    if (appliedFilters.estateId) {
      filtered = filtered.filter((batch) => batch.estateId === appliedFilters.estateId);
    }

    if (appliedFilters.status) {
      filtered = filtered.filter((batch) => batch.status === appliedFilters.status);
    }

    if (appliedFilters.showSingleItemOnly) {
      filtered = filtered.filter((batch) => batch.hasSingleItem);
    }

    if (appliedFilters.showNearDeadlineOnly) {
      filtered = filtered.filter((batch) => batch.isNearDeadline);
    }

    if (appliedFilters.showManualOnly) {
      filtered = filtered.filter((batch) => batch.isManualProcessing);
    }

    const searchTerm = String(appliedFilters.search || '').trim().toLowerCase();
    if (searchTerm) {
      filtered = filtered.filter(
        (batch) =>
          batch.batchNumber.toString().includes(searchTerm) ||
          batch.estate.name.toLowerCase().includes(searchTerm),
      );
    }

    setFilteredBatches(filtered);
  }, [batches, appliedFilters]);

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      const [statsResponse, batchesResponse] = await Promise.all([
        get('/fire-insurance-batches/dashboard/stats'),
        get('/fire-insurance-batches/dashboard/list'),
      ]);

      setStats(statsResponse);
      setBatches(
        (batchesResponse || []).map((batch: BatchDashboardItem) => ({
          ...batch,
          isManualProcessing: batch.isManualProcessing ?? false,
        })),
      );
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
      dangerNotification('Erro', 'Erro ao carregar dados do dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterSearch = (values: FilterValues) => {
    setAppliedFilters(values);
  };

  const handleFilterClear = () => {
    setAppliedFilters(initialFilterValues);
  };

  const handleDownload = async (batchId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDownloadingBatchId(batchId);
    try {
      await downloadYelumSpreadsheet(batchId);
    } catch (error) {
      dangerNotification(
        'Erro',
        (error as Error)?.message || 'Erro ao baixar planilha do lote',
      );
    } finally {
      setDownloadingBatchId(null);
    }
  };

  const handleBatchClick = (batchId: string) => {
    history.push(`${match.url}/${batchId}`);
  };

  const handleToggleManualProcessing = async (
    batch: BatchDashboardItem,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();

    if (batch.status !== 'OPENED') {
      return;
    }

    const enabling = !batch.isManualProcessing;
    const confirmMessage = enabling
      ? 'Este lote deixará de ser transmitido automaticamente pela Yelum. O fechamento deverá ser feito manualmente. Deseja continuar?'
      : 'A transmissão automática será reativada para este lote (se ainda estiver aberto e dentro do prazo). Deseja continuar?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setTogglingBatchId(batch.id);
    try {
      await setBatchManualProcessing(batch.id, enabling);
      setBatches((prev) =>
        prev.map((b) =>
          b.id === batch.id ? { ...b, isManualProcessing: enabling } : b,
        ),
      );
      setStats((prev) => {
        if (!prev) return prev;
        const delta = enabling ? 1 : -1;
        const wasTransmissionToday =
          batch.status === 'OPENED' &&
          moment(batch.transmissionDate).isSame(moment(), 'day');
        return {
          ...prev,
          batchesManualProcessing: Math.max(
            0,
            (prev.batchesManualProcessing ?? 0) + delta,
          ),
          transmissionsToday:
            wasTransmissionToday
              ? Math.max(0, (prev.transmissionsToday ?? 0) + (enabling ? -1 : 1))
              : prev.transmissionsToday,
        };
      });
      successNotification(
        'Sucesso',
        enabling
          ? `Transmissão automática bloqueada para o lote #${batch.batchNumber}`
          : `Transmissão automática reativada para o lote #${batch.batchNumber}`,
      );
    } catch (error) {
      dangerNotification(
        'Erro',
        (error as Error)?.message || 'Erro ao atualizar processamento do lote',
      );
    } finally {
      setTogglingBatchId(null);
    }
  };

  const handleOpenEditBatch = (batch: BatchDashboardItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingBatch(batch);
  };

  const handleOpenTransmitBatch = (batch: BatchDashboardItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setTransmittingBatch(batch);
  };

  const handleOpenSyncBatchNumbering = (batch: BatchDashboardItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setSyncingBatch(batch);
  };

  const handleEditBatchSuccess = (updated: UpdatedBatchCodesResponse) => {
    setBatches((prev) =>
      prev.map((item) =>
        item.id === updated.id
          ? {
              ...item,
              quotationCode: updated.quotationCode,
              proposalCode: updated.proposalCode,
              policyNumber: updated.policyNumber,
              status: updated.status,
            }
          : item,
      ),
    );
  };

  const handleExportConsolidated = async () => {
    if (filteredBatches.length === 0) {
      dangerNotification('Atenção', 'Nenhum lote para exportar com os filtros atuais.');
      return;
    }

    setExportingConsolidated(true);
    try {
      const batchIds = filteredBatches.map((b) => b.id);
      const result = await downloadYelumConsolidatedSpreadsheet(batchIds);
      successNotification(
        'Sucesso',
        `Exportação concluída: ${result.processed} lote(s) processado(s), ${result.skipped} ignorado(s) (sem itens).`,
      );
    } catch (error) {
      dangerNotification(
        'Erro',
        (error as Error)?.message || 'Erro ao gerar planilha consolidada',
      );
    } finally {
      setExportingConsolidated(false);
    }
  };

  const manualProcessingCount = batches.filter(
    (b) => b.status === 'OPENED' && b.isManualProcessing,
  ).length;

  if (isLoading) {
    return (
      <PageContainer>
        <Loader active inline="centered" size="large">
          Carregando dashboard...
        </Loader>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {downloadingBatchId ? (
        <Dimmer active inverted page>
          <Loader indeterminate>Baixando planilha do lote...</Loader>
        </Dimmer>
      ) : null}
      <Grid>
        <Grid.Row>
          <Grid.Column>
            <h1>
              <Icon name="clipboard list" />
              Dashboard de Lotes - Yelum Seguros
            </h1>
          </Grid.Column>
        </Grid.Row>

        {/* Cards de Estatísticas */}
        <Grid.Row>
          <Grid.Column width={16}>
            <StatsCardContainer>
              <Card.Group itemsPerRow={5} stackable>
                <StatsCard color="blue">
                  <Card.Content textAlign="center">
                    <Statistic size="small" color="blue">
                      <Statistic.Value>{stats?.totalOpenBatches || 0}</Statistic.Value>
                      <Statistic.Label>Lotes Abertos</Statistic.Label>
                    </Statistic>
                  </Card.Content>
                </StatsCard>

                <StatsCard color="yellow">
                  <Card.Content textAlign="center">
                    <Statistic size="small" color="yellow">
                      <Statistic.Value>
                        {stats?.batchesWithSingleItem || 0}
                      </Statistic.Value>
                      <Statistic.Label>Com 1 Item</Statistic.Label>
                    </Statistic>
                    <Popup
                      trigger={<Icon name="info circle" color="grey" />}
                      content="Lotes que possuem apenas 1 item. A Yelum exige mínimo de 2 itens para transmissão."
                      position="top center"
                      on="click"
                      hideOnScroll
                    />
                  </Card.Content>
                </StatsCard>

                <StatsCard color="orange">
                  <Card.Content textAlign="center">
                    <Statistic size="small" color="orange">
                      <Statistic.Value>
                        {stats?.batchesNearDeadline || 0}
                      </Statistic.Value>
                      <Statistic.Label>Próximos do Prazo</Statistic.Label>
                    </Statistic>
                    <Popup
                      trigger={<Icon name="info circle" color="grey" />}
                      content="Lotes com 5 dias ou menos até a data de transmissão"
                      position="top center"
                      on="click"
                      hideOnScroll
                    />
                  </Card.Content>
                </StatsCard>

                <StatsCard color="green">
                  <Card.Content textAlign="center">
                    <Statistic size="small" color="green">
                      <Statistic.Value>
                        {stats?.transmissionsToday || 0}
                      </Statistic.Value>
                      <Statistic.Label>Transmissões Hoje</Statistic.Label>
                    </Statistic>
                  </Card.Content>
                </StatsCard>

                <StatsCard color="purple">
                  <Card.Content textAlign="center">
                    <Statistic size="small" color="purple">
                      <Statistic.Value>{manualProcessingCount}</Statistic.Value>
                      <Statistic.Label>Processamento Manual</Statistic.Label>
                    </Statistic>
                    <Popup
                      trigger={<Icon name="info circle" color="grey" />}
                      content="Lotes abertos com transmissão automática bloqueada."
                      position="top center"
                      on="click"
                      hideOnScroll
                    />
                  </Card.Content>
                </StatsCard>
              </Card.Group>
            </StatsCardContainer>
          </Grid.Column>
        </Grid.Row>

        {/* Filtros */}
        <Grid.Row>
          <Grid.Column width={16}>
            <FilterBar
              fields={filterFields}
              initialValues={initialFilterValues}
              onSearch={handleFilterSearch}
              onClear={handleFilterClear}
              syncToUrl
              urlNamespace="fire-batches"
              actions={
                <>
                  <Button basic color="blue" onClick={loadDashboardData}>
                    <Icon name="refresh" />
                    Atualizar
                  </Button>
                  <Button
                    color="teal"
                    onClick={handleExportConsolidated}
                    loading={exportingConsolidated}
                    disabled={exportingConsolidated || filteredBatches.length === 0}
                  >
                    <Icon name="file excel" />
                    Exportar
                  </Button>
                </>
              }
            />
          </Grid.Column>
        </Grid.Row>

        {/* Tabela de Lotes */}
        <Grid.Row>
          <Grid.Column width={16}>
            <TableContainer>
              <h3>
                Lotes ({filteredBatches.length})
                {filteredBatches.length !== batches.length && (
                  <span style={{ fontSize: '14px', color: '#888', marginLeft: 10 }}>
                    (de {batches.length} total)
                  </span>
                )}
              </h3>
              <Table celled striped selectable>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Lote #</Table.HeaderCell>
                    <Table.HeaderCell>Imobiliária</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">Status</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">Itens</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">Data Transmissão</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">Dias Restantes</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">Vencimento</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">Alertas</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">Ações</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {filteredBatches.length === 0 ? (
                    <Table.Row>
                      <Table.Cell colSpan={9} textAlign="center">
                        <Icon name="inbox" size="huge" color="grey" />
                        <p>Nenhum lote encontrado com os filtros aplicados.</p>
                      </Table.Cell>
                    </Table.Row>
                  ) : (
                    filteredBatches.map((batch) => (
                      <ClickableTableRow
                        key={batch.id}
                        $manualOpen={batch.status === 'OPENED' && batch.isManualProcessing}
                        onClick={() => handleBatchClick(batch.id)}
                        warning={batch.status !== 'CLOSED' && batch.hasSingleItem}
                        negative={
                          batch.status !== 'CLOSED' &&
                          batch.isNearDeadline &&
                          batch.hasSingleItem &&
                          !batch.isManualProcessing
                        }
                      >
                        <Table.Cell>
                          <strong>#{batch.batchNumber}</strong>
                        </Table.Cell>
                        <Table.Cell>{batch.estate.name}</Table.Cell>
                        <Table.Cell textAlign="center">
                          <Label color={getBatchStatusColor(batch.status) as any} size="small">
                            {translateBatchStatus(batch.status)}
                          </Label>
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          <strong>{batch.itemCount}</strong>
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          {moment(batch.transmissionDate).format('DD/MM/YYYY')}
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          {batch.status === 'CLOSED' || batch.status === 'CANCELLED' ? (
                            '-'
                          ) : batch.daysRemaining > 0 ? (
                            <Label
                              color={batch.daysRemaining <= 5 ? 'orange' : 'green'}
                              size="small"
                            >
                              {batch.daysRemaining} {batch.daysRemaining === 1 ? 'dia' : 'dias'}
                            </Label>
                          ) : (
                            <Label color="red" size="small">
                              Atrasado
                            </Label>
                          )}
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          {batch.dueDate ? `Dia ${batch.dueDate}` : '-'}
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          {batch.isManualProcessing && (
                            <Popup
                              trigger={
                                <AlertBadge color="purple" size="small">
                                  <Icon name="hand paper" />
                                  Manual
                                </AlertBadge>
                              }
                              content="Transmissão automática bloqueada. Fechamento deve ser feito manualmente na Yelum."
                              position="top center"
                              on="click"
                              hideOnScroll
                            />
                          )}
                          {/* Não mostrar alertas para lotes fechados */}
                          {batch.status !== 'CLOSED' && batch.hasSingleItem && (
                            <Popup
                              trigger={
                                <AlertBadge color="yellow" size="small">
                                  <Icon name="warning sign" />1 item
                                </AlertBadge>
                              }
                              content="Lote com apenas 1 item. Mínimo necessário: 2 itens."
                              position="top center"
                              on="click"
                              hideOnScroll
                            />
                          )}
                          {batch.status !== 'CLOSED' &&
                            batch.isNearDeadline &&
                            !batch.isManualProcessing && (
                            <Popup
                              trigger={
                                <AlertBadge color="orange" size="small">
                                  <Icon name="clock" />
                                  Urgente
                                </AlertBadge>
                              }
                              content="Faltam 5 dias ou menos para a transmissão."
                              position="top center"
                              on="click"
                              hideOnScroll
                            />
                          )}
                          {batch.status === 'CLOSED' && (
                            <Label color="grey" size="small">
                              Finalizado
                            </Label>
                          )}
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          <ActionsCell>
                            <AuthorizationContainer
                              action={ActionEnum.save}
                              feature={FeatureEnum.fireinsurances}
                            >
                              <Button
                                icon
                                basic
                                size="small"
                                color={batch.isManualProcessing ? 'green' : 'orange'}
                                title={
                                  batch.status !== 'OPENED'
                                    ? 'Ação disponível apenas para lotes abertos'
                                    : batch.isManualProcessing
                                      ? 'Reativar transmissão automática'
                                      : 'Bloquear transmissão automática'
                                }
                                disabled={
                                  batch.status !== 'OPENED' ||
                                  (togglingBatchId !== null && togglingBatchId !== batch.id)
                                }
                                loading={togglingBatchId === batch.id}
                                onClick={(e) => handleToggleManualProcessing(batch, e)}
                              >
                                <Icon name={batch.isManualProcessing ? 'unlock' : 'lock'} />
                              </Button>
                            </AuthorizationContainer>
                            <AuthorizationContainer
                              action={ActionEnum.save}
                              feature={FeatureEnum.fireinsurances}
                            >
                              <Button
                                icon
                                basic
                                size="small"
                                color="blue"
                                title="Editar lote"
                                onClick={(e) => handleOpenEditBatch(batch, e)}
                              >
                                <Icon name="edit" />
                              </Button>
                            </AuthorizationContainer>
                            <AuthorizationContainer
                              action={ActionEnum.save}
                              feature={FeatureEnum.fireinsurances}
                            >
                              <Button
                                icon
                                basic
                                size="small"
                                color="purple"
                                title={
                                  canTransmitBatch({
                                    status: batch.status,
                                    isManualProcessing: batch.isManualProcessing,
                                    itemCount: batch.itemCount,
                                  }).reason || 'Transmitir lote'
                                }
                                disabled={
                                  !canTransmitBatch({
                                    status: batch.status,
                                    isManualProcessing: batch.isManualProcessing,
                                    itemCount: batch.itemCount,
                                  }).allowed
                                }
                                onClick={(e) => handleOpenTransmitBatch(batch, e)}
                              >
                                <Icon name="paper plane" />
                              </Button>
                            </AuthorizationContainer>
                            <AuthorizationContainer
                              action={ActionEnum.save}
                              feature={FeatureEnum.fireinsurances}
                            >
                              <Button
                                icon
                                basic
                                size="small"
                                color="teal"
                                title={
                                  canSyncBatchNumbering({
                                    status: batch.status,
                                    itemCount: batch.itemCount,
                                    quotationCode: batch.quotationCode,
                                    insurerCode: batch.insurer?.code,
                                  }).reason || 'Sincronizar numeração'
                                }
                                disabled={
                                  !canSyncBatchNumbering({
                                    status: batch.status,
                                    itemCount: batch.itemCount,
                                    quotationCode: batch.quotationCode,
                                    insurerCode: batch.insurer?.code,
                                  }).allowed
                                }
                                onClick={(e) => handleOpenSyncBatchNumbering(batch, e)}
                              >
                                <Icon name="sync" />
                              </Button>
                            </AuthorizationContainer>
                            <Button
                              icon
                              size="small"
                              title="Baixar lote (formato Yelum)"
                              disabled={
                                batch.itemCount === 0 ||
                                (downloadingBatchId !== null && downloadingBatchId !== batch.id)
                              }
                              loading={downloadingBatchId === batch.id}
                              onClick={(e) => handleDownload(batch.id, e)}
                            >
                              <Icon name="download" />
                            </Button>
                          </ActionsCell>
                        </Table.Cell>
                      </ClickableTableRow>
                    ))
                  )}
                </Table.Body>
              </Table>
            </TableContainer>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <EditBatchCodesModal
        open={Boolean(editingBatch)}
        batch={editingBatch}
        onClose={() => setEditingBatch(null)}
        onSuccess={handleEditBatchSuccess}
      />

      <TransmitBatchConfirmModal
        open={Boolean(transmittingBatch)}
        batch={transmittingBatch}
        onClose={() => setTransmittingBatch(null)}
        onSuccess={() => loadDashboardData()}
      />

      <SyncBatchNumberingModal
        open={Boolean(syncingBatch)}
        batch={syncingBatch}
        onClose={() => setSyncingBatch(null)}
        onSuccess={() => loadDashboardData()}
      />
    </PageContainer>
  );
};

const mapStateToProps = (state: iRootState) => ({
  availableBrokersAndEstates: state.availableBrokersAndEstates,
});

export default connect(mapStateToProps)(FireInsuranceBatchesDashboard);

